import { IcClose } from '@assets/icons/24x24';
import { IcInfo } from '@assets/icons/32x32';
import { Button, Dialog, HStack, Icon, IconButton, Separator, Text, VStack } from '@chakra-ui/react';
import { ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

export type CockpitDialogProps = {
  open: boolean;
  children: ReactNode;
  onConfirm?: () => void;
  headerLabel?: string;
  isLoading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  isDisabled?: boolean;
  showContentInfo?: boolean;
  showCancelButton?: boolean;
  size?: 'xs' | 'sm' | 'md';
  otherChildren?: ReactNode;
  onCancel?: () => void;
};

const widthFromSize = {
  xs: '350px',
  sm: '476px',
  md: '516px',
};

export default function CockpitDialog({
  open,
  children,
  onConfirm,
  headerLabel,
  isLoading,
  confirmLabel,
  cancelLabel,
  isDisabled = false,
  showContentInfo = true,
  showCancelButton = true,
  size = 'md',
  otherChildren,
  onCancel,
}: Readonly<CockpitDialogProps>) {
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = '';
    };
  }, []);

  return (
    <Dialog.Root open={open} unmountOnExit lazyMount placement="center" closeOnInteractOutside={false} size={size}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content userSelect="none">
          <VStack
            backgroundColor="neutral.black"
            width={widthFromSize[size]}
            border="2px solid"
            borderColor="neutral.black"
            gap={0}
          >
            <HStack width="100%" backgroundColor="neutral.800" justifyContent="flex-end">
              {headerLabel && (
                <Text textTransform="uppercase" marginLeft={2} marginRight="auto" color="neutral.200">
                  {headerLabel}
                </Text>
              )}
              {onCancel && (
                <IconButton variant="transparent" boxSize="40px" aria-label="cancel" onClick={onCancel}>
                  <Icon as={IcClose} boxSize="24px" />
                </IconButton>
              )}
            </HStack>
            <Separator borderColor="neutral.900" />
            <VStack gap={size === 'md' ? 5 : 3} width="100%" backgroundColor="neutral.700" padding={5} paddingTop={3}>
              <HStack alignItems="start" width="100%" gap={3}>
                {showContentInfo && <Icon as={IcInfo} color="neutral.300" boxSize="32px" />}
                <VStack width="100%" gap={2}>
                  {children}
                </VStack>
              </HStack>
              {otherChildren}
              {((onCancel && showCancelButton) || onConfirm) && (
                <HStack gap={2} width={(onCancel && showCancelButton) || size === 'md' ? 'auto' : '100%'}>
                  {onCancel && showCancelButton && (
                    <Button size="md" flex={1} onClick={onCancel} disabled={isLoading}>
                      {cancelLabel ?? <FormattedMessage id="global.cancel" />}
                    </Button>
                  )}
                  {onConfirm && (
                    <Button
                      size="md"
                      flex={1}
                      loading={isLoading}
                      disabled={isLoading || isDisabled}
                      onClick={() => !(isLoading || isDisabled) && onConfirm()}
                    >
                      {confirmLabel ?? <FormattedMessage id="global.confirm" />}
                    </Button>
                  )}
                </HStack>
              )}
            </VStack>
          </VStack>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
